// src/pages/FacturePage.jsx
import React from 'react';
import {
  Card,
  Typography,
  Button,
  Row,
  Col,
  Divider,
  Tag,
  Space,
  Statistic,
  Descriptions,
  Image,
  Table
} from 'antd';
import {
  ArrowLeftOutlined,
  PrinterOutlined,
  DownloadOutlined,
  MailOutlined,
  HomeOutlined,
  PhoneOutlined,
  UserOutlined,
  CarOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// Logo de l'entreprise (remplacer par votre propre logo)
const companyLogo = 'https://via.placeholder.com/150x80/0a6b5a/ffffff?text=Cimenterie+Mali';

const FacturePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  
  // Récupérer les données de l'achat depuis la navigation ou utiliser des données par défaut
  const achatData = location.state?.achat || {
    id: 'ACH-001',
    designation: 'Ciment CPJ35',
    nomChauffeur: 'Moussa Diallo',
    matriculeVoiture: 'AB-123-CD',
    destination: 'Bamako',
    statut: 'Livré',
    quantite: 1000,
    prixUni: 5500,
    date: '2024-01-15',
    total: 5500000,
    dateLivraison: '2024-01-18',
    note: 'Livraison effectuée avec succès'
  };

  // Informations de l'entreprise
  const companyInfo = {
    name: 'Cimenterie Mali SA',
    address: 'Rue du Commerce, Bamako, Mali',
    phone: '+223 20 21 22 23',
    email: 'contact@cimenteriemali.ml',
    website: 'www.cimenteriemali.ml',
    rc: 'RC-BKO-2024-001',
    nif: 'NIF-0012456789'
  };

  // Informations du client
  const clientInfo = {
    name: 'Entreprise de Construction Moderne',
    address: 'Quartier du Fleuve, Bamako',
    contact: 'M. Issa Konaté',
    phone: '+223 76 45 32 10',
    email: 'contact@ecm.ml'
  };

  // Données de la facture
  const factureData = {
    numero: `FACT-${achatData.id}`,
    dateEmission: dayjs().format('DD/MM/YYYY'),
    dateEcheance: dayjs().add(30, 'days').format('DD/MM/YYYY'),
    conditions: 'Paiement à 30 jours',
    tva: 18, // TVA en pourcentage
    timbre: 1000 // Timbre fiscal en FCFA
  };

  // Calcul des montants
  const montantHT = achatData.total;
  const montantTVA = (montantHT * factureData.tva) / 100;
  const montantTimbre = factureData.timbre;
  const montantTTC = montantHT + montantTVA + montantTimbre;

  // Format monétaire
  const formatMoney = (amount) => {
    return amount.toLocaleString('fr-FR') + ' FCFA';
  };

  // Colonnes du tableau des articles
  const columns = [
    {
      title: 'Désignation',
      dataIndex: 'designation',
      key: 'designation',
      width: 300,
    },
    {
      title: 'Quantité',
      dataIndex: 'quantite',
      key: 'quantite',
      align: 'center',
      width: 100,
    },
    {
      title: 'Prix Unitaire',
      dataIndex: 'prixUni',
      key: 'prixUni',
      align: 'right',
      render: (prix) => formatMoney(prix),
      width: 150,
    },
    {
      title: 'Total HT',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (total) => (
        <Text strong className="text-primary-500">
          {formatMoney(total)}
        </Text>
      ),
      width: 150,
    },
  ];

  // Données du tableau (un seul article pour cette facture)
  const tableData = [
    {
      key: '1',
      designation: achatData.designation,
      quantite: achatData.quantite,
      prixUni: achatData.prixUni,
      total: achatData.total,
    }
  ];

  // Fonction pour imprimer la facture
  const handlePrint = () => {
    window.print();
  };

  // Fonction pour télécharger la facture
  const handleDownload = () => {
    // Ici vous pourriez générer un PDF
    alert('Fonctionnalité de téléchargement PDF à implémenter');
  };

  // Fonction pour envoyer par email
  const handleEmail = () => {
    alert('Fonctionnalité d\'envoi email à implémenter');
  };

  return (
    <div className="p-5 bg-gray-50 min-h-screen print:bg-white print:p-0">
      <div className="mb-5 print:hidden">
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/achat')}
          className="mb-4"
        >
          Retour aux Achats
        </Button>
        
        <Space className="float-right">
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            Imprimer
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleDownload} type="primary" className="btn-primary">
            Télécharger PDF
          </Button>
          <Button icon={<MailOutlined />} onClick={handleEmail}>
            Envoyer par Email
          </Button>
        </Space>
      </div>

      {/* Carte principale de la facture */}
      <Card className="modern-card mb-6 print:shadow-none print:border-0">
        {/* En-tête de la facture */}
        <Row gutter={[24, 24]} className="mb-8">
          <Col span={12}>
            <div className="mb-4">
              <Image
                src={companyLogo}
                alt="Logo"
                width={150}
                preview={false}
                className="mb-2"
              />
              <Title level={3} className="m-0 text-primary-500">
                {companyInfo.name}
              </Title>
            </div>
            
            <Space direction="vertical" size="small">
              <div className="flex items-center">
                <HomeOutlined className="mr-2 text-gray-500" />
                <Text>{companyInfo.address}</Text>
              </div>
              <div className="flex items-center">
                <PhoneOutlined className="mr-2 text-gray-500" />
                <Text>{companyInfo.phone}</Text>
              </div>
              <div className="flex items-center">
                <MailOutlined className="mr-2 text-gray-500" />
                <Text>{companyInfo.email}</Text>
              </div>
              <div>
                <Text strong>RC: </Text>
                <Text>{companyInfo.rc}</Text>
              </div>
              <div>
                <Text strong>NIF: </Text>
                <Text>{companyInfo.nif}</Text>
              </div>
            </Space>
          </Col>
          
          <Col span={12} className="text-right">
            <div className="mb-6">
              <Tag color="blue" className="text-base py-2 px-4">
                <Title level={2} className="m-0">
                  FACTURE
                </Title>
              </Tag>
            </div>
            
            <Space direction="vertical" size="middle" align="end">
              <div>
                <Text strong>N° Facture: </Text>
                <Text type="secondary">{factureData.numero}</Text>
              </div>
              <div>
                <Text strong>Date d'émission: </Text>
                <Text>{factureData.dateEmission}</Text>
              </div>
              <div>
                <Text strong>Date d'échéance: </Text>
                <Text>{factureData.dateEcheance}</Text>
              </div>
              <div>
                <Text strong>Conditions: </Text>
                <Text>{factureData.conditions}</Text>
              </div>
            </Space>
          </Col>
        </Row>

        <Divider />

        {/* Informations du client et de la livraison */}
        <Row gutter={[24, 24]} className="mb-8">
          <Col span={12}>
            <Title level={4} className="mb-4">
              <UserOutlined className="mr-2" />
              Client
            </Title>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Nom">{clientInfo.name}</Descriptions.Item>
              <Descriptions.Item label="Adresse">{clientInfo.address}</Descriptions.Item>
              <Descriptions.Item label="Contact">{clientInfo.contact}</Descriptions.Item>
              <Descriptions.Item label="Téléphone">{clientInfo.phone}</Descriptions.Item>
              <Descriptions.Item label="Email">{clientInfo.email}</Descriptions.Item>
            </Descriptions>
          </Col>
          
          <Col span={12}>
            <Title level={4} className="mb-4">
              <CarOutlined className="mr-2" />
              Livraison
            </Title>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Chauffeur">
                <Tag icon={<UserOutlined />} color="blue">
                  {achatData.nomChauffeur}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Matricule">
                <Tag icon={<CarOutlined />} color="green">
                  {achatData.matriculeVoiture}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Destination">
                <Tag color="purple">{achatData.destination}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                <Tag 
                  color={achatData.statut === 'Livré' ? 'green' : 'orange'}
                  icon={achatData.statut === 'Livré' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                  className="text-sm py-1 px-3"
                >
                  {achatData.statut}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Date commande">
                <Tag icon={<CalendarOutlined />}>
                  {dayjs(achatData.date).format('DD/MM/YYYY')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Date livraison">
                <Tag icon={<CalendarOutlined />}>
                  {achatData.dateLivraison ? dayjs(achatData.dateLivraison).format('DD/MM/YYYY') : 'En attente'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>

        <Divider />

        {/* Tableau des articles */}
        <div className="mb-8">
          <Title level={4}>Détail de la commande</Title>
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={false}
            className="modern-table"
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2} align="right">
                  <Text strong>Total HT:</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <Text strong>-</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <Text strong className="text-base text-primary-500">
                    {formatMoney(montantHT)}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </div>

        {/* Récapitulatif des montants */}
        <Row gutter={24}>
          <Col span={16}></Col>
          <Col span={8}>
            <div className="bg-gray-50 p-6 rounded-lg">
              <Space direction="vertical" size="middle" className="w-full">
                <Row justify="space-between">
                  <Text>Total HT:</Text>
                  <Text>{formatMoney(montantHT)}</Text>
                </Row>
                <Row justify="space-between">
                  <Text>TVA ({factureData.tva}%):</Text>
                  <Text>{formatMoney(montantTVA)}</Text>
                </Row>
                <Row justify="space-between">
                  <Text>Timbre fiscal:</Text>
                  <Text>{formatMoney(montantTimbre)}</Text>
                </Row>
                <Divider className="my-2" />
                <Row justify="space-between">
                  <Text strong className="text-base">Total TTC:</Text>
                  <Text strong className="text-lg text-primary-500">
                    {formatMoney(montantTTC)}
                  </Text>
                </Row>
              </Space>
            </div>
          </Col>
        </Row>

        {/* Informations additionnelles */}
        <Divider />
        
        <div className="mt-8">
          <Title level={4}>Notes et informations</Title>
          <Card size="small" className="bg-blue-50">
            <Text>{achatData.note || 'Aucune note particulière pour cette commande.'}</Text>
          </Card>
        </div>

        {/* Signature */}
        <div className="mt-12 text-center">
          <Row gutter={24}>
            <Col span={12}>
              <div className="border-t-2 border-gray-400 pt-4 w-4/5 mx-auto">
                <Text strong>Le Client</Text>
                <br />
                <Text type="secondary">Signature et cachet</Text>
              </div>
            </Col>
            <Col span={12}>
              <div className="border-t-2 border-primary-500 pt-4 w-4/5 mx-auto">
                <Text strong>La Cimenterie Mali SA</Text>
                <br />
                <Text type="secondary">Signature et cachet</Text>
              </div>
            </Col>
          </Row>
        </div>

        {/* Statistiques supplémentaires */}
        <div className="mt-10">
          <Title level={4}>Informations supplémentaires</Title>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Quantité totale"
                  value={achatData.quantite}
                  suffix="sacs"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Prix moyen/sac"
                  value={achatData.prixUni}
                  suffix="FCFA"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic
                  title="Total HT"
                  value={achatData.total}
                  suffix="FCFA"
                  valueStyle={{ color: '#0a6b5a' }}
                />
              </Card>
            </Col>
          </Row>
        </div>
      </Card>

      {/* Styles d'impression via Tailwind */}
      <style>
        {`
          @media print {
            @page {
              margin: 20mm;
            }
            
            body * {
              visibility: hidden;
            }
            
            .ant-card, .ant-card * {
              visibility: visible;
            }
            
            .ant-card {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              box-shadow: none !important;
              border: none !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default FacturePage;